import { controlServiceImpl, ToggleVal } from "@lvt/streaming";
import {
    unwrap,
    WebData,
    webDataErrorSimple,
    webDataLoading,
    webDataNotAsked,
    WebDataSteps,
    webDataSuccess,
} from "@lvt/web-data";
import { Fab, CircularProgress, Grid, Tooltip } from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import clsx from "clsx";
import { useEffect, useState } from "react";

import { getPatrolState } from "@/api/cameras";
import client from "@/api/client";
import useActionButtonStyles from "@/common/components/streaming/ActionButtons/ActionButton.styles";
import { RootState, useDispatch, useSelector } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import * as FullStory from "@/utilities/hooks/fullstory";

import SuspendPatrolModal from "./SuspendPatrolModal/SuspendPatrolModal";


// interface SuspendPatrolButtonProps {
// }

const SuspendPatrolButton: React.FC = () => {
    const actionButtonClasses = useActionButtonStyles();
    const [patrolIsSuspended, setPatrolIsSuspended] = useState<WebData<boolean>>(webDataNotAsked());
    const [suspendPatrolDuration, setSuspendPatrolDuration] = useState<string>("5");
    const [patrolModalIsOpen, setPatrolModalIsOpen] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const streamingId = useSelector((state: RootState) => state.cameras.streamingId);
    const dispatch = useDispatch();

    const controlService = controlServiceImpl(client, streamingId!); // streamingId should never be null

    useEffect(() => {
        if (streamingId) fetchPatrolState(streamingId);
    }, [streamingId]);

    const fetchPatrolState = async (cameraId: number) => {
        setPatrolIsSuspended(webDataLoading());

        try {
            const res = await getPatrolState(cameraId);

            setPatrolIsSuspended(webDataSuccess(res));
        } catch (e) {
            setPatrolIsSuspended(webDataErrorSimple({ message: JSON.stringify(e) }));
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: `Unable to fetch patrol state: ${e}`,
                }),
            );
        }
    };

    const resumePatrol = async () => {
        setIsProcessing(true);

        try {
            await controlService.suspendPatrol(ToggleVal.On);
        } catch (e) {
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: `Failed to resume patrol: ${e}`,
                }),
            );
        } finally {
            if (streamingId) fetchPatrolState(streamingId);

            setIsProcessing(false);
        }
    };

    const suspendPatrol = async (streamingId: number) => {
        setIsProcessing(true);

        try {
            await controlService.suspendPatrol(ToggleVal.Off, parseInt(suspendPatrolDuration));
        } catch (e) {
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: `Failed to suspend patrol: ${e}`,
                }),
            );
        } finally {
            if (streamingId) fetchPatrolState(streamingId);

            setIsProcessing(false);
        }

        setPatrolModalIsOpen(false);
    };

    const handleSuspendPatrol = () => {
        unwrap(patrolIsSuspended) ? resumePatrol() : setPatrolModalIsOpen(true);
    };

    return (
        <Grid item className={clsx(actionButtonClasses.fabItem, FullStory.names.classes.streaming.suspendPatrol)}>
            {streamingId && (
                patrolIsSuspended.type === WebDataSteps.Loading ? (
                    <Fab className={actionButtonClasses.fab} color="primary">
                        <CircularProgress size={22} color="secondary" />
                    </Fab>
                ) : (
                    <>
                        <Tooltip PopperProps={{ disablePortal: true }} title="Suspend Patrol" placement="top">
                            <Fab
                                id={"streaming_suspend-patrol-button"}
                                className={actionButtonClasses.fab}
                                onClick={() => handleSuspendPatrol()}
                            >
                                {unwrap(patrolIsSuspended) ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </Fab>
                        </Tooltip>
                        <SuspendPatrolModal
                            handleSuspend={suspendPatrol}
                            handleInput={setSuspendPatrolDuration}
                            handleVisible={setPatrolModalIsOpen}
                            streamingId={streamingId}
                            duration={suspendPatrolDuration}
                            visible={patrolModalIsOpen}
                            isProcessing={isProcessing}
                        />
                    </>
                )
            )}
        </Grid>
    );
};

export default SuspendPatrolButton;
